import React from "react";
import { Link } from "react-router-dom";
import tb from '../../tb.png'
import tb1 from '../../tb1.png'
import tb2 from '../../tb2.png'

const PrivacyPolicy = () => {
  return (
    <section className="terms-wrapper">
      <div className="terms-container">
        <h1 className="terms-title">PawPlate Privacy Policy</h1>
        <p className="terms-intro">Last updated: 2025-05-22</p>

        <p>
          Opami LLC, located at 1111B S Governors Ave STE 25056, Dover, DE, 19904, United States <b>("PawPlate," "we," or "us")</b> are committed to protecting your privacy. This Privacy Policy describes how we collect, use, share, and secure your personal information when you use PawPlate's websites, mobile applications, and download any downloadable software, products, and services offered at <a href="https://pawplate.app"> https://pawplate.app/</a> and <a href="https://pawplate.io"> https://pawplate.io/</a> (collectively, the <b>"PawPlate Service",  the "Service"</b>). It also explains your rights and choices regarding your personal information.

        </p>

        <p>
          We encourage you to read this Policy carefully. Please also read PawPlate <Link to="/tems-of-use">Terms of Use</Link>. If you have any questions, please contact us at <a href="mailto:support@pawplate.io">support@pawplate.io</a>.
        </p>

        <p>
         <b> Note:</b> Our Privacy Policy may be modified from time-to-time. If we make material changes to this Policy, we will notify you (e.g. via email or by means of a notice on this website) before they take effect. We encourage you to periodically review this page for the latest information on our privacy practices. Your continued use of PawPlate Service after updates signifies acceptance of the revised Policy.
        </p>
      <p>
         <b>Global Compliance:</b> We designed this Policy to meet requirements of privacy laws around the world, including the EU/UK General Data Protection Regulation (GDPR), the California Consumer Privacy Act as amended by the CPRA (CCPA/CPRA), Canada’s Personal Information Protection and Electronic Documents Act (PIPEDA), Australia’s Privacy Act and Australian Privacy Principles (APPs), Brazil’s Lei Geral de Proteção de Dados (LGPD), and Mexico’s Federal Law on Protection of Personal Data (LFPDPPP), among others. If any additional provisions apply to you based on your country or state, see the region-specific sections below.

      </p>
      <strong>
      <p>
      By using PawPlate Service, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with our practices, please do not use the app or website. 

      </p>
      </strong>
        <h3 className="terms-heading">Data Controller and Contact Information</h3>
        <p>
          Opami LLC (1111B S Governors Ave STE 25056, Dover, DE, 19904, United States) is the data controller for personal information we process. You can reach us at <a href="mailto:support@pawplate.io">support@pawplate.io</a> for any privacy-related inquiries.
        </p>

        <h3 className="terms-heading">Key Points Summary</h3>
        <p>
          To make things easy, here are the key privacy points at a glance. You can click on each topic to see more details in the full policy below:
        </p>

  
        <ul className="terms-list">
          <li><strong>Information We Collect:</strong> We collect personal information that you provide (such as your name, contact info, payment details, and pet profile including pet health data), data generated through your use of PawPlate Service (like feeding activity and app usage stats), and technical data (like device identifiers, cookies, and IP address). We do not collect your precise geolocation or any human biometric identifiers. See Information We Collect for more details.
          </li>
          <li><strong>How We Use Information:</strong> We use your data to provide and improve our Services – for example, to create your account, customize feeding recommendations for your pet, process orders/payments, send you updates, and support you. We may also use data to personalize your experience, profile your pet’s nutritional needs, and suggest products or tips, but no automated decision will ever have legal or significant effects on you (the profiling is solely to help you care for your pet). We only use your information for the purposes described and in accordance with the law. See How We Use Your Information for a list of purposes and legal bases.
          </li>
          <li><strong>Cookies & Tracking:</strong> We use cookies and similar technologies on our site (and analytic tools in our app) to make PawPlate Service work, remember your preferences, and understand how people use our services. Some are essential for functionality, and others (like analytics cookies) help us improve or, with your consent, personalize content. We do not use cookies for third-party advertising without your permission. You can control cookies through your browser settings and opt-out of analytics as described in Cookies and Tracking Technologies.
          </li>
          <li><strong>When We Share Information:</strong> We do not sell your personal information to third parties. We share data only in limited situations: with service providers who help us run PawPlate Service (under strict data protection obligations), with payment processors for transactions, with your consent or at your direction, or if legally required (e.g. responding to law enforcement or protecting rights). If we ever need to share data for new purposes, we will obtain your consent when required. See How We Share Your Information for details on our third-party disclosures.
          </li>
          <li><strong>International Data Transfers:</strong> PawPlate is based in the United States, so your information will likely be transferred to and stored on servers in the U.S. We protect these cross-border data transfers by using legally approved safeguards such as Standard Contractual Clauses (SCCs) and, where applicable, by certifying under the EU-U.S. Data Privacy Framework. We apply similar protections for transfers from the UK, Canada, Brazil, Mexico and other regions. See International Data Transfers for how we keep your data safe when it moves globally.
        </li>
        <li><strong>Your Rights and Choices:</strong> You have control over your personal information. Depending on your location, you may have the right to access a copy of your data, correct it, delete it, object to or restrict certain processing, or get an explanation of automated decisions. All users can adjust their communication preferences (e.g. opt out of marketing emails) and withdraw any consent given, and we will never punish you for exercising your rights (no account changes or price differences). California residents can opt out of any “sale” or “sharing” of their data (although we don’t currently do that), and have rights to know and delete data. EU/UK individuals have extensive data subject rights (access, erasure, etc.), and similar rights exist under PIPEDA, LGPD, Mexico’s law, and others. We explain how to exercise these rights in Your Privacy Rights and Choices and in the region-specific sections. Typically, you can reach us at <a href="mailto:support@pawplate.io">support@pawplate.io</a> to make any request, and we will respond within the timeframes required by law (e.g. 1 month under GDPR , 45 days under CCPA, 15 days under LGPD ).
        </li>
        <li><strong>Data Security & Breach Notification:</strong> We secure your personal data using industry-standard measures (encryption, access controls, etc.) to prevent loss or unauthorized access. If a data breach ever occurs that compromises your personal information, we will notify you and the appropriate authorities as required by law. For example, GDPR requires us to inform regulators within 72 hours of learning of a significant breach , and to promptly inform affected individuals if the breach is likely to result in high risk. We are committed to complying with all breach notification laws (including those in California, Canada, Australia, Brazil, and Mexico) and to keeping you informed of any material security issues. See Data Security and Breach Response for more on our protections and how we handle incidents.
        </li>
        <li><strong>Privacy Officer & Contacts:</strong> You can find all contact details in Contact Us. We encourage you to reach out with any questions or if you need to exercise your rights. If you feel we haven’t resolved your privacy concern, you also have the right to contact your local data protection authority (e.g. a GDPR supervisory authority, the California Privacy Protection Agency, the Office of the Privacy Commissioner of Canada, the Brazilian ANPD, Mexico’s INAI, etc.).
        </li>
        </ul>
        <strong>
        <p>Please continue reading for the full details of our Privacy Policy, which is organized into the following sections for your convenience:</p>
        </strong>
        <ul className="terms-list">
          <li>
            <a href="#info-collect">Information We Collect</a>
          </li>
          <li>
            <a href="#how-we-use">How We Use Your Information</a>
          </li>
          <li>
            <a href="#info-collect">Cookies and Tracking Technologies</a>
</li>
          <li>
              <a href="#how-we-share">How We Share Your Information</a>
</li>
          <li>
             <a href="#data"> International Data Transfers</a>

             </li>
          <li>
           <a href="#retention"> Data Retention</a>
          </li>
          <li>
       <a href="#privacy">  Your Privacy Rights and Choices</a>

          </li>
          <li>
            <a href="#security"> Data Security and Breach Response </a>

          </li>
          <li>
           <a href="#children">  Children’s Privacy </a>
          </li>
          <li>
           <a href="#changes">  Changes to this Policy</a>
          </li>
          <li>
         <a href="#contact">  Contact Us</a>

          </li>
          <li>
            <a href="#jurisdiction">Jurisdiction-Specific Notices (EU/UK, California, Canada, Australia, Brazil, Mexico)</a>
          </li>
        </ul>
         <h3 className="terms-heading" id="info-collect">Information We Collect</h3>
          <p>We collect information that you provide directly to us, information that is collected automatically about your use of PawPlate Service, and information from third parties (if you use those features). This includes personal information about you and information about your pet. We limit our collection to what is relevant for the purposes described in this Policy.</p>
            <p>The types of information we may collect are:</p>
            <ul>
              <li>
              <strong>Account and Contact Information: </strong>When you sign up or interact with PawPlate Service, you provide information like your email address, and login credentials. If you choose to provide it, we may also collect other contact details. This allows us to create your account, communicate with you, and personalize your experience.

              </li>
              <li>
                <strong>Pet Profile and Health Data: </strong>A core feature of PawPlate Service is tracking your pet’s nutritional needs. You may provide details about your pet such as name, species (e.g. dog, cat), breed, age, weight, sex, and health information (e.g. dietary restrictions, allergies, medical conditions, activity level). This pet health data helps us generate tailored feeding recommendations. While this information is about your pet (not you), we treat it with care and protect it as part of your account. Note: Pet health data is not considered human medical data or biometric data, and it is not used to identify you – it’s used solely to serve your pet’s needs.

              </li>
              <li>
                 <strong>Payment and Transaction Data: </strong>If you make a purchase through PawPlate Service, we may collect limited billing-related information, such as the last four digits of your payment card, the card brand (e.g., Visa, Mastercard), and transaction receipts to verify your subscription status. Important: PawPlate uses compliant third-party payment processors to handle your payment card data – we do not store your full card number or CVV on our own servers (only a token or the last four digits may be retained for reference). We also keep records of your purchases, order history, and receipts for accounting and to help you track your orders.

              </li>
              <li>
                 <strong>Usage Data: </strong> We automatically collect information about how you use our app and website. This includes features you use, pages or screens you view, date and time of your activities, and other interaction data. This usage data helps us understand what is useful to you and improve our service.

              </li>
              <li>
                 <strong>Device and Technical Data: </strong>Like most apps and websites, we gather technical information when you use PawPlate Service. This can include your device type, operating system and version, browser type (for web users), screen resolution, unique device identifiers, language preference, and crash logs. We also record your Internet Protocol (IP) address, which may give a general idea of your location (e.g. city or country). Additionally, our app may collect mobile device IDs or advertising identifiers (such as Apple’s IDFA or Google’s Advertising ID) if your device settings allow. We use this data for debugging, security (e.g. preventing fraud), and analytics about our user base.

              </li>
              <li>
                 <strong>Cookies and Similar Technologies:</strong>When you visit our website, we use “cookies” and related tracking technologies. Cookies are small text files stored on your browser. They allow us to recognize you, remember your preferences (like language or login info), and understand how you navigate our site. We also use cookies or SDKs in conjunction with analytics tools (like Google Analytics) to collect information about site traffic, usage patterns, and the effectiveness of features or marketing. Some cookies may collect identifiers like cookie IDs or device IDs and usage info. For more details, see Cookies and Tracking Technologies below.

              </li>
              <li>
                 <strong>Third-Party Integrations: </strong>If you choose to connect PawPlate Service to external services or third parties, we may receive information from them. For example, if we offer a “Sign in with Google/Apple” option and you use it, we receive your name and email from that service (with your permission). If we ever obtain public data (for instance, if you post a review or tag us on social media), we handle that according to this policy as well.

              </li>
              <li>
                 <strong>Sensitive Personal Information: </strong>We do not actively collect sensitive personal data about you like your racial or ethnic origin, health or genetic data about you, biometric identifiers, or precise geolocation of you. We only request information that is needed for pet care and running our Service.

              </li>
              <li>
                 <strong>Children’s Data: </strong>PawPlate is not intended for use by children under the age of 13 (or under the relevant age of consent in your region). We do not knowingly collect personal information from children. For more, see Children’s Privacy.

              </li>
              
            </ul>

            <p>
            
            </p>
            <p>
              We collect the information above either directly from you (when you fill in forms or profiles, etc.), automatically through your use of the Service (via technology like cookies, as you navigate or use features), or from authorized third parties. In all cases, we aim to minimize the data collected to only what we truly need. You are free to choose not to provide certain information, but bear in mind this may limit your ability to use some PawPlate Service features (for example, if you don’t provide an email, we cannot create an account; if you don’t provide pet details, we cannot generate feeding plans).
            </p>

         <h3 className="terms-heading" id="how-we-use">How We Use Your Information</h3>
         <p>
          We use personal information for purposes that make our Service work for you – primarily to provide and improve the PawPlate app/website and to support your pet’s nutrition goals. We ensure that we have a valid legal reason (lawful basis) for each use of personal data, especially for users in jurisdictions like the EU. Below we list the purposes for which we process data, with examples. Where applicable, we also note the legal basis for those purposes under GDPR and similar laws.

         </p>
          <ul>
              <li>
              <strong>To Provide Our Services and Features:</strong>We process data to create and manage your account, authenticate you when you log in, and provide you with the functionalities of PawPlate Service. This includes using your and your pet’s information to generate feeding recommendations, diet plans, and reminders as part of the Service. We also use contact info to deliver transactional communications, notifying you about important account or Service updates, and responding to your inquiries. 
              Legal basis: This use is generally necessary to perform our contract with you (GDPR Art. 6(1)(b)) – we can’t provide the promised Service without this data.


              </li>
              <li>
               <strong>Personalized Nutrition Recommendations (Profiling):</strong>PawPlate uses the information about your pet (age, breed, weight, health, etc.) along with your feeding history to profile and analyze what nutrition or product may benefit your pet. For example, we might suggest adjusting portion sizes or trying a certain nutrient supplement. This profiling is fully automated in generating suggestions, but does not produce any outcome that significantly affects you or your rights – it’s meant to assist you, and you can always choose whether or not to follow the recommendations. 
                Legal basis: For EU users, this profiling is part of the Service you requested (contractual necessity) and/or our legitimate interest (Art. 6(1)(f)) in providing innovative, customized features to improve pet care. We ensure any such processing respects your rights and you can contact us to ask questions or opt out of personalized suggestions if you wish.

              </li>
              <li>
               <strong>Processing Payments and Orders:</strong>We use payment and contact data to facilitate purchases or subscriptions you make. We also use information to prevent fraudulent transactions and to keep proper records of sales. 
Legal basis: This is part of performing the contract with you for your purchase (GDPR Art. 6(1)(b)), and for complying with legal obligations (Art. 6(1)(c)) related to financial record-keeping, taxes, and fraud prevention.

              </li>
              <li> 

                <strong>Communicating With You:</strong>We use your contact information to communicate with you about the Service. This includes sending you service-related messages. We may also send you customer support communications if you reach out with a question or issue. 
Legal basis: These communications are part of our contractual service and our legitimate interests in keeping you informed about your account and responding to you (Art. 6(1)(f)).

              </li>
              <li>
                <strong>Marketing and Newsletters (Opt-In):</strong>If you explicitly subscribe to our newsletter or promotional updates, we will use your email to send you news about PawPlate Service features, offers, or pet nutrition tips. We may also send you in-app promotions or ask for your feedback via surveys. You can opt out of marketing emails at any time by clicking “unsubscribe” in the email or contacting us – we will honor your choice promptly. We will not spam you or use dark patterns to pressure you into staying subscribed. 
Legal basis: We rely on your consent (GDPR Art. 6(1)(a)) for sending marketing communications in jurisdictions that require consent (e.g. EU, Canada’s anti-spam laws). In some cases, if you are an existing customer, we may rely on our legitimate interest (Art. 6(1)(f)) to send you modest updates about similar products or services, but you will always have a clear chance to opt out, and we will respect opt-out preferences (as required under laws like CAN-SPAM, CASL, or the ePrivacy Directive).

              </li>
              <li>
                <strong>Analytics and Product Improvement:</strong> We process usage, device, and cookie data to understand how our services are used and to improve them. This includes analyzing trends, such as which features are most popular or where users encounter issues, so we can make PawPlate Service better. We might generate aggregate statistics to guide product decisions or inform our content. We also track and debug performance (for example, monitoring app crashes or load times) to ensure a smooth experience. 
Legal basis: It is our legitimate interest to analyze and improve our services continuously (Art. 6(1)(f) GDPR). We use this data in a way that does not override your privacy rights – for instance, analytics are typically viewed in aggregate form, and we may employ privacy measures like pseudonymization. In some jurisdictions, certain analytics cookies require consent – we will obtain consent when required before collecting those (see the Cookies section).

              </li>
              <li>
                <strong>Security and Fraud Prevention:</strong> Information we collect (especially technical and device data) is used to protect the security of PawPlate Service, our users, and others. We monitor and may process data like IP addresses, device identifiers, and account activity to detect and prevent fraudulent activity, abuse of our platform, malware, or other harmful actions. We also may use your data to verify your identity when you exercise your rights or if we suspect unauthorized access to your account. 
Legal basis: These activities are in our legitimate interest (Art. 6(1)(f) GDPR) to keep our service safe and secure, and to meet legal obligations (Art. 6(1)(c)) such as data security requirements under various laws. We consider security fundamental and do not use security data for unrelated purposes.

              </li>
              <li>
                <strong>Security and Fraud Prevention:</strong>Finally, we may process any of your personal information as needed to comply with applicable laws and regulations. This includes responding to valid legal requests (e.g. court orders, subpoenas) or cooperating with authorities, and using your data to exercise or defend legal claims. We also keep certain records to comply with laws (for example, tax laws may require retention of transaction data, or privacy laws may require we document your consent or requests). 
Legal basis: Such processing is necessary for compliance with legal obligations (GDPR Art. 6(1)(c)) or, in some cases, for our legitimate interests in asserting legal rights or handling legal issues (Art. 6(1)(f)).

              </li>
            </ul>
          <p>
            We will use your personal information only for the purposes above or for compatible purposes. If we need to use it for a wholly different purpose, we’ll seek your consent or inform you of the new purpose where required. We do not engage in automated decision-making, including profiling, that produces legal effects or similarly significant effects on you – as noted, our automated profiling is limited to suggestions for your pet’s benefit and can be ignored or overridden by you.
          </p>
           <h3 className="terms-heading">How We Use Your Information</h3>
           <p>We use cookies and similar technologies to enhance your experience on our website. Some are necessary for the site to function, and others help us improve or personalize content. We also may use analytics tools to understand usage. You have choices to control or disable cookies.</p>
           <p>
            <strong>What are cookies?</strong> Cookies are small data files placed on your browser or device when you visit a website. They allow the site to recognize your browser and remember certain information about you (such as your preferences or login status). Other related technologies include web beacons (pixel tags), which are tiny images embedded in a site or email that can track if you’ve viewed content, and software development kits (SDKs) in mobile apps that function similarly to cookies.

           </p>
           <p>
            <strong>How PawPlate uses cookies: </strong> On our website, we use cookies for a few reasons:
           </p>
           <ul>
            <li>
              <strong>
      Necessary Cookies:
              </strong>
          These are essential for the website to work. You cannot opt out of these because the site won’t function properly without them.

            </li>
               <li>
              <strong>
Preferences Cookies:
              </strong>
              These cookies remember your choices to give you a more personalized experience. For instance, a cookie might save your settings so you don’t have to set them every time.

            </li>
               <li>
              <strong>
Analytics Cookies:
              </strong>
              We use these to collect information about how visitors use our site, so we can improve it. For example, we might use Google Analytics or similar tools that set cookies to see which pages are popular, how long users stay, how they arrived at our site, and if any errors occurred. The information collected is typically aggregated and doesn’t directly identify you. It helps us understand user behavior in general. We might also use in-app analytics SDKs within our mobile application for similar purposes.

            </li>
               <li>
              <strong>
        Advertising Cookies:
              </strong>
              Currently, PawPlate does not host third-party ads on our site, and we do not use third-party advertising cookies to track you for ad targeting. We may promote our own content or products on our site, but those don’t involve third-party cookies.

            </li>                                
           </ul>
          <p>
            <strong>
        Consent for cookies:
              </strong>
              You can always adjust your cookie preferences by adjusting your browser settings. You can also withdraw it at any time (for example, by clearing cookies or using our site’s cookie control to opt out of analytics).

          </p>
           <p>
            <strong>
Controlling cookies: 
              </strong>
              Most web browsers let you control cookies through their settings preferences. You can typically block or delete cookies. However, if you disable all cookies (including essential ones) some site features may not work. Therefore, we recommend allowing at least the necessary cookies. For analytics cookies, you can choose to disable them without affecting core functionality. To opt out of Google Analytics specifically, Google provides an opt-out browser add-on.

          </p>
           <p>
            <strong>
Do Not Track and Global Privacy Controls:
              </strong>
              “Do Not Track” (DNT) is a setting in some browsers that signals a preference not to be tracked across websites. Currently, there is no universal standard for how to interpret DNT signals. As such, our site does not respond to DNT headers in a uniform way. However, we respect more specific global opt-out signals where required. For instance, California law requires businesses to honor a Global Privacy Control (GPC) signal as a valid opt-out of sale/sharing. While we do not sell or share personal data for advertising, we have configured our site to recognize GPC signals – if detected, we will treat it in accordance with California requirements (e.g. applying it to disable any third-party trackers that could be considered a “sale” or “share” of data).

          </p>
            <p>
            <strong>
Mobile app tracking: 
              </strong>
              In our mobile app, cookies per se are not used, but we might use device identifiers and in-app analytics. For example, Apple iOS and Google Android provide advertising identifiers which can be used by analytics SDKs. You can control the use of these identifiers in your device settings (for instance, by selecting “Limit Ad Tracking” on iOS or “Opt out of Ads Personalization” on Android, which will reduce tracking). Our app will also ask for any necessary permissions and you can allow or deny those.

          </p>
            <p>
            <strong>
Third-party websites and cookies:
              </strong>
               If our website contains links to third-party websites or if you navigate to a third-party service from PawPlate (for example, to view an article or process a payment), those services may set their own cookies. This Privacy Policy does not cover third-party sites. We encourage you to read the privacy notices of any external site you visit. For instance, when you make a payment, you might be redirected to our payment processor’s page which may use cookies for fraud prevention and transaction processing – those would be governed by the processor’s policy.

          </p>
          <p>
            In summary, we strive to be transparent about our use of cookies and tracking. We only deploy what we need and give you options to control them. We do not sneak in any trackers without telling you or use any design tricks to obtain consent. If you have any questions about our cookie practices, feel free to contact us.

          </p>
        <h3 className="terms-heading" id="how-we-share">How We Share Your Information</h3>
        <p>
          We do not sell your personal information to data brokers or random third parties. When we do share information, it’s mainly with trusted service providers who perform tasks for us (under strict agreements), or in a few other lawful contexts like mergers or legal requirements. We share the minimum necessary data and always with safeguards. Below are the categories of recipients with whom we may share personal information, and why.

        </p>
        <ul>
          <li>
            <strong>
              Service Providers (Processors):
            </strong>
            We use third-party companies to help us run PawPlate Service efficiently. These service providers act under our instructions and only for our specified purposes. Examples include cloud hosting providers (to store data and keep the app/website running), email service providers, analytics services (to help us understand usage patterns), payment processors (to handle credit card transactions securely), and customer support tools (to manage support tickets or chat if we have one). When we share data with service providers, we limit it to what they need to perform their function. They are contractually obligated to protect your information, keep it confidential, and not use it for their own purposes. For instance, if we use a cloud server provider in another country, we would have a data processing agreement and (if applicable) Standard Contractual Clauses to protect any personal data transferred.

          </li>
           <li>
            <strong>
            Business Transfers: 
            </strong>
            If we undergo a business transaction such as a merger, acquisition by another company, sale of all or part of our assets, or in the unlikely event of bankruptcy, user information (including personal data) may be among the assets transferred to the buyer or successor. If that happens, we will ensure that your personal data remains protected and is used consistently with this Privacy Policy. We will also provide notice to users (for example, via email or a notice on our site/app) if your data will be subject to a new privacy policy or if the new owner will use your data for new purposes. You will have the opportunity to opt out of continued use of your data if laws require.

          </li>
           <li>
            <strong>
Legal Obligations and Safety:
            </strong>
            We may disclose personal information to third parties (such as government authorities, law enforcement, or private parties) when we believe in good faith that such disclosure is necessary to:
            <ul>
              <li>
                Comply with a legal obligation or request (e.g. responding to subpoenas, court orders, or lawful requests by public authorities, including to meet national security or law enforcement requirements).

              </li>
              <li>
                Protect and defend our rights or property, or the rights, property, or safety of our users or others. This could include investigating and helping prevent security issues, fraud or abuse; enforcing our Terms of Service or other agreements; or collecting owed fees.

              </li>
              <li>
                Respond to an emergency if we believe in good faith that it’s required to prevent imminent harm.

              </li>
            </ul>
          </li>
          <li>
            In such cases, we will only share the information that is reasonably necessary and will object to overly broad or unlawful requests. When permitted, we may inform you if your information was requested by authorities (except in cases where we’re legally prohibited or where notifying you could defeat the purpose of the request).

          </li>
           <li>
            <strong>
              With Your Consent or at Your Direction: 
            </strong>
            We will share your personal information with others if you explicitly ask or consent to us doing so. For instance, if you decide to publish a testimonial or share your pet’s success story on our platform or social media, we might post your name and pet information with your permission.

          </li>
           <li>
            <strong>
              Advertising and Analytics Partners:
            </strong>
            As noted, we do not share personal data with advertisers in the sense of selling it or allowing third parties to track you for their own marketing when you use PawPlate. We may, however, share certain aggregated or de-identified information with partners for legitimate business purposes. For example, we might tell a pet food brand, “X% of PawPlate Service users have senior dogs,” without revealing any personal identities, just to explore a sponsorship or content partnership. Aggregated statistics cannot be linked back to any individual. If we ever decide to run third-party ads or a program that involves sharing more identifiable data, we will update our practices and give you the ability to opt out or opt in as required.

          </li>
           <li>
            <strong>
              No Sale of Personal Information: 
            </strong>
            We want to re-emphasize that we do not sell your personal information. “Selling” in privacy terms usually means exchanging personal data for money or other value. We don’t do that. We also do not “share” your personal info for cross-context behavioral advertising (as defined under California law). If that ever changes, we will notify you and provide a clear ability to opt out (like a “Do Not Sell or Share” link) in accordance with laws like the CCPA/CPRA.

          </li>

        <li>
          <b>
            Service Provider Disclosure:
          </b>
            Some of the service providers we use may involve data transfers to other countries or jurisdictions (for example, a cloud service in the EU or an email service in the US if you are elsewhere). Wherever our providers are, they are chosen carefully for their security and privacy standards. We ensure each provider is bound by a contract to use personal data only for our purposes and to protect it. They cannot use your data for their independent marketing and cannot “sell” your data onward.

        </li>
         <li>
          <b>
Joint Offerings:
          </b>
          If we ever run a joint promotion or co-sponsored event where a third party is involved (say a pet health webinar with another company), and as part of that you agree to share info with them, we will make that clear at sign-up. You would then also be subject to the other party’s privacy policy for the data you give them. We will avoid any hidden disclosures – you’ll know who is getting your data if it’s not just PawPlate.

        </li>
        </ul>

      <p>
        If you have questions about third parties that may have access to your data, let us know. We can provide a current list of key subprocessors (like major infrastructure or email providers) upon request.

      </p>

        <h3 className="terms-heading" id="data">International Data Transfers</h3>
        <p>
          PawPlate operates globally. When you use our services, your personal information may be transferred to and processed in countries other than your own. Most of our core systems are in the EU, so data from around the world is sent to the EU or US. We understand that some countries have different data protection laws than others – for example, if you are in the European Economic Area (EEA), UK, or other regions with strict data laws, your data is protected by those laws even when it travels abroad. We take steps to ensure any cross-border transfer of personal data complies with applicable legal requirements and that adequate safeguards are in place to protect your information.

        </p>
        <p>
          <b>
            Data locations:
          </b> The information we collect from you will likely be stored and processed on servers in the USA. It may also be processed in other countries where our service providers have operations or backups – for instance, if we use a European or US cloud server for some functionality or if our support team operates in Canada, etc. The specific locations can vary, but we require the same level of security and confidentiality regardless of location.

        </p>
        <p>
          <b>
            Privacy protections for international transfers:
          </b> Whenever we transfer personal data out of its country of origin, we will ensure a similar degree of protection is afforded to it by implementing at least one of the following safeguards:

        </p>
        <ul>
         <li>
          <b>
            Standard Contractual Clauses:
          </b>  If you are in the EEA, UK, or Switzerland, and your data is transferred to a country not deemed by your regulators to have “adequate” data protection (such as the U.S.), we rely on European Commission-approved Standard Contractual Clauses (SCCs) or the UK International Data Transfer Agreement/Addendum as appropriate. These are legal contracts that obligate the recipient of the data to protect your personal data to EU/UK standards. They also give you enforceable rights regarding your data. We have SCCs in place for relevant transfers, supplemented by additional security measures (like encryption in transit and at rest) as needed.

        </li>
         <li>
          <b>
            Other Adequacy Mechanisms: 
          </b> We also monitor and utilize any other lawful bases for transfer. For example, Canada allows transfers if protected by contractual agreements consistent with PIPEDA’s requirements. Brazil’s LGPD permits transfers to countries with adequate protection or with contractual clauses guaranteeing compliance. In Mexico, consent may be required for certain transfers unless an exception applies – we will obtain consent if needed. Where applicable, we might also rely on your explicit consent for a transfer (e.g. if you’re informed of the risks and agree), but as a policy we prefer systemic protections like contracts so you don’t have to rely solely on consent.

        </li>
         <li>
          <b>
            Necessary for Contract:
          </b> In some situations, international transfer is inherently necessary to provide you the service you signed up for. For instance, sending your data to our U.S. servers is part of the contract to use PawPlate Service. GDPR acknowledges this as a valid transfer mechanism (Art.49(1)(b) – transfer necessary for performance of a contract with the data subject). However, we primarily use this basis only when other safeguards aren’t feasible, because we prefer to ensure robust protection via the measures above.

        </li>
     

        </ul>
        <p>
          No matter where we process your data, we apply the same security standards and privacy controls. We also evaluate the risk of government access in destination countries and take steps to mitigate any undue risks (for example, using encryption to protect data from unauthorized access). Our goal is that your data enjoys continuous protection from the moment it leaves your device to the moment it’s stored in our databases and beyond, regardless of geographic location.

        </p>
        <p>
          If you would like more information about our international data transfer practices, feel free to contact us.

        </p>

        <p>

        </p>
        <p> <b>Specific notes for certain regions:</b></p>
        <ul>
          <li>
            EU/EEA and UK: As described, we use SCCs and comply with the EU-U.S.

          </li>
          <li>
            Canada: Personal data of Canadian users may be transferred outside of Canada for processing and storage. Such data may be subject to the laws of those foreign jurisdictions (for example, accessible by government authorities under lawful orders). However, our contracts with service providers ensure a level of protection comparable to Canadian law. By using PawPlate Service, you consent to such cross-border transfers, but we ensure they are done securely.

          </li>
          <li>
          Australia: If we transfer personal information overseas (which we do), we take reasonable steps to ensure the overseas recipients do not breach the Australian Privacy Principles in relation to that information (APP 8.1). Alternatively, we might rely on exceptions in the Privacy Act (e.g. if the transfer is required by contract with you or with your consent, or the recipient is subject to a law or scheme comparable to Australian law). Practically, our approach is to treat all personal data with the highest standard of care, regardless of location.

          </li>
          <li>
          Brazil: We comply with LGPD requirements for international transfers. We will only transfer data out of Brazil if we have one of the LGPD-approved mechanisms in place (such as SCC-like contractual clauses ensuring LGPD principles and rights are upheld, an adequacy decision if one exists for the destination country, or other grounds allowed by Article 33 of LGPD). 

          </li>
        </ul>
        <p> 
          In summary, your information may travel internationally, but we are guiding its journey with legal and security protections so that it remains as safe as it would be at home.

        </p>

         <h3 className="terms-heading" id="retention">Data Retention</h3>

        <p>
          We keep your personal information only for as long as necessary to fulfill the purposes for which we collected it, including for satisfying any legal, accounting, or reporting requirements. We do not keep your data indefinitely “just because” – when we no longer need personal information, we will delete it or anonymize it. The exact duration we retain data varies by data type and user choices.

        </p>
        <p>
          Our general retention practices are:
        </p>
        <ul>
          <li>
            <b>
        Account Information:
            </b> If you have an account with PawPlate, we will retain your personal information for as long as your account is active. This allows us to provide the Service to you continuously. If you choose to delete your account or if it’s been inactive for a long period, we will initiate deletion of your personal data. Typically, after you delete your account (or request deletion), we will remove or anonymize personal data within approximately 30 days, barring unforeseen technical delays. Some residual information may persist in our backups or caches for a short period (generally up to 30-60 additional days) before those are overwritten – during that time, your data is not accessible in production and remains protected. We reserve the right to retain a minimal set of information to document that you had an account and that we fulfilled your deletion request (for compliance purposes), such as a record of your email with a flag “account deleted on X date,” but we won’t retain detailed profile or pet info after deletion.

          </li>
          <li>
            <b>
          Pet and Health Data:
            </b>   Information about your pet (profiles, feeding history) is part of your account data and is treated the same way – kept while you use the Service and removed upon deletion. We might retain aggregated data derived from pet info (e.g. average feeding amounts across all users) for statistical purposes after deletion, but such aggregated data will contain nothing identifiable to you.

          </li>
          <li>
            <b>
        Transaction and Payment Records:
            </b> If you made purchases or transactions through PawPlat Service, we may need to retain certain information about those transactions for longer periods. This is to comply with financial regulations, tax laws, and for audit purposes. For example, U.S. tax law and accounting rules might require us to keep invoices and payment records for a number of years (commonly 7 years). Therefore, while your account may be deleted, we might keep a record that “User X purchased item Y on date Z for $ amount” in our financial logs until the legal retention period lapses. We secure this info and limit its use to those compliance purposes.

          </li>
          <li>
            <b>
            Communications and Support Tickets:
            </b> If you contacted us for support or we corresponded via email, we may retain those communications as long as needed to address your issue and for a short period thereafter in case you have follow-up questions. Certain communications (especially if they involve a complaint or legal issue) might be kept longer to establish a paper trail of how we interacted, which can be important for legal accountability.

          </li>
          <li>
            <b>
              Analytics Data: 
            </b> Usage data that we collect for analytics is often aggregated or anonymized, and we might retain aggregate analytics indefinitely since it no longer identifies any individual. For example, overall app usage trends or number of monthly active users can be stored to measure growth. Raw analytics logs, however, that may contain personal identifiers (like IP addresses) are typically rotated or deleted after a shorter period (perhaps 14 months by default for some analytics tools, or as configured by us). We don’t keep detailed web logs forever – they are periodically purged.

          </li>
          <li>
            <b>
          Legal Hold and Disputes:
            </b> In the event of a legal dispute or if we must enforce our terms, we might need to retain relevant information until the issue is resolved. For instance, if we ban an account for fraud, we may keep certain data to support any investigation or to block the user from creating new accounts. If a law enforcement request or legal hold is in place directing us not to delete data, we will retain the data as required. Once the hold is lifted or the matter resolved, we will proceed with deletion as appropriate.

          </li>
          <li>
            <b>
              Compliance Retention: 
            </b> Various laws might stipulate minimum retention periods for certain data. For example, as mentioned, financial and transaction data for X years, or records of consent for a certain time. We adhere to those laws. On the other hand, some laws (like the CCPA/CPRA in California) encourage informing users of how long we intend to keep categories of data. In general, aside from specific cases noted above, we try not to keep personal data more than we reasonably need. In many cases, you have control – if you withdraw consent for something (like marketing), we will stop using and eventually delete that data, and if you delete your account, we remove the majority of data as described.

          </li>
          <li>
            <b>
Backups:
            </b> We maintain encrypted backups of our databases for disaster recovery. Those backups are kept for a limited time on a rolling basis. This means your data might remain in backups even after deletion for that limited backup retention window. We protect those backups carefully and only restore from them if necessary (e.g. to recover from a server failure). When they expire, they are deleted. We do not use backup data for any active purpose unless needed for recovery.

          </li>
        </ul>
        <p>
          In all cases, when we retain data, we do so in accordance with this Privacy Policy and all applicable laws. Once the retention period expires, we will securely erase or anonymize the personal data. We also periodically review the data we hold and delete or anonymize records that are no longer needed.

        </p>
        <p>
          If you have specific questions about our data retention practices for a certain type of information, you can contact us for more detail. We can also honor requests (if legally permissible) to delete certain data earlier – for example, if you don’t want to delete your whole account but want us to delete a specific piece of information, we will try to accommodate that, provided it’s feasible and not required to be retained.

        </p>
         <h3 className="terms-heading" id="privacy">Your Privacy Rights and Choices</h3>
        <p>
          You have rights to know about, access, correct, and even delete your personal information in many jurisdictions. You also have choices about how your data is used – for example, you can opt out of marketing emails, decline certain cookies, or disable certain data collection. We are committed to honoring your rights and not using any tricks or obstacles (no unfair default settings, no unnecessary steps) to dissuade you from exercising them. Below, we outline the general rights you have and how to exercise them. In the Jurisdiction-Specific Notices section, we provide additional details that apply under certain laws (like GDPR, CCPA, etc.), but we summarize the main points here.

        </p>
        <p>
         <b> Access and Portability:</b> You have the right to request that we confirm whether we are processing your personal information and to access that information. This includes letting you know the types of data we have about you and, upon request, providing you with a copy of the personal data in a common format.
        </p>
        <p>
          <b>
            Correction (Rectification): 
          </b> We strive to keep your information accurate and up-to-date. If you find that any personal information we have is incorrect or incomplete, you have the right to request that we correct or update it. In many cases, you can do this yourself – for instance, you can log into your account and edit your profile details or pet information at any time. If there’s something you can’t edit, contact us and we will rectify it. We will verify the new data and make the change promptly.

        </p>
        <p>
          <b>
          Deletion (Erasure/Cancellation): 
          </b> You can ask us to delete your personal information. This is sometimes called the “right to be forgotten.” It covers both the EU concept of erasure and similar rights in other laws (like the CCPA deletion right or Mexico’s cancellation). We will honor such requests by deleting your data from our active systems, except where retention is permitted or required by law (as explained in Data Retention). Deletion requests can be made by contacting us. We might ask you to confirm (since deletion is permanent) and to verify your identity (to prevent someone else from deleting your account maliciously). Once confirmed, we will erase the data within the time frame allowed by law and will inform you when it’s done. Some data may remain in backup for a short time but we will also delete it according to our backup schedule. If a certain exception applies (for example, we cannot delete data that is needed for an ongoing legal claim or that we must keep for a legal obligation), we will let you know.

        </p>
        <p>
          <b>
            Withdrawal of Consent:
          </b> In situations where we rely on your consent to process data, you have the right to withdraw that consent at any time. For instance, if you gave consent for receiving marketing emails or for processing of optional data, you can revoke it. Withdrawing consent will not affect the lawfulness of processing done before the withdrawal. It just means we will stop the specific processing going forward. You can usually withdraw by changing settings (like unsubscribing from emails or toggling off a feature in the app) or by contacting us. We have made our consent flows such that declining consent is as easy as giving it – no hidden settings or complicated unsubscription gymnastics.

        </p>
        <p>
          <b>
            Objection to Processing:
          </b>  In certain cases, you have the right to object to our processing of your personal information. If you object, and the law requires us to honor it, we will stop processing your data for that purpose. This is most applicable under GDPR/UK GDPR and similar laws:

        </p>
        <ul>
          <li>
            <b>
Direct Marketing: 
            </b> You can always object to or opt out of direct marketing from us. If you say “stop sending me promotional messages,” we will stop. This is absolute and at no cost to you.

          </li>
          <li>
            <b>
              Legitimate Interests:
            </b> If we are processing your data on the basis of “legitimate interests,” you have the right to object if you have a particular reason related to your situation. 

          </li>
          <li>
          In some jurisdictions, “opposition” is a formal right (like the “O” in Mexico’s ARCO rights – Derecho de Oposición). It similarly allows you to object to processing of your data, such as when you don’t want to be in a certain database. We will evaluate such requests in line with applicable law.

          </li>
        </ul>
        <p>
          <b>
            Restriction of Processing:
          </b> You may have the right to request that we limit the processing of your data in certain scenarios. For instance, if you contest the accuracy of data, you can ask us to pause processing (besides just storing it) until we resolve the accuracy issue. Or if you need data for a legal claim but we were about to delete it, you can ask that we keep it but not process it further. We will mark such data as “restricted” and only process it for specific reasons (like with your consent or for legal claims) as allowed.

        </p>
        <p>
          <b>
      Data Portability:
          </b> For data that you provided to us and that we process by automated means on the basis of consent or contract, you have the right to get that data in a structured, commonly used, machine-readable format. You can also ask us to transmit it to another controller where technically feasible. In simpler terms, this means you could ask for an export of your profile and pet data to, say, a CSV or JSON file if you wanted to port to another service. We will accommodate these requests as far as we can.

        </p>
        <p>
          <b>
Automated Decisions:
          </b>  As noted, we do not make any automated decisions about you that have legal or similarly significant effects. 

        </p>
        <p>
          <b>
          Authorized Agents: 
          </b> If you are a resident of certain regions (like California), you can designate an authorized agent to make requests on your behalf (such as a lawyer or a relative). We will process requests from authorized agents if they provide proof of authorization (e.g. a signed letter from you, or power of attorney) and we can verify their identity and possibly directly confirm with you that you permit the request. This is to prevent fraud. For instance, we wouldn’t want an impostor pretending to be an “agent” and deleting your account without your knowledge. We follow the regulations on this process (CCPA, for example, has guidelines for verifying agent requests).

        </p>
        <p>
          <b>
Non-Discrimination / Fair Treatment:
          </b> We will not discriminate against you for exercising any of these rights. That means we won’t deny you service, give you a worse experience, or charge you different prices just because you exercised your privacy rights.

        </p>
        <p>
          <b>
Exercising Your Rights:
          </b> To exercise the rights above, you can contact us in any of the ways described in Contact Us. 

        </p>
        <p>
          <b>
Response Time:
          </b>  We will respond to your request as quickly as we can, generally within one month. If it’s a simple request (like opting out of emails), it might be much faster (within a few days). If it’s a more complex request (like a large data access request) or if we have a high volume of requests, we may need more time. Many laws allow an extension (for example, GDPR allows up to an additional two months for complex cases, CCPA allows a total of 90 days in some cases). If we need an extension, we will inform you of the reason and the extension period. For instance, under LGPD in Brazil we would respond within 15 days to confirm receipt and either satisfy the request or give a timeline. Under Mexico’s law, we would respond within 20 business days and implement a granted request within 15 more business days. We are aware of these timelines and strive to beat them when possible.
        </p>
        <p>
          <b>
        Limited Exceptions:
          </b> Occasionally, we might deny a request or fulfill it only partially, if an exception applies. For example, if you request deletion but we are required by law to keep certain data, we will explain that to you. Or if fulfilling an access request would adversely affect others’ privacy or our trade secrets (like revealing source code of algorithms), we might not include those parts. We will always justify the basis for any denial. Generally, we aim to fulfill requests to the fullest extent possible.

        </p>
        <p>
          <b>
        Complaints:
          </b>  We hope to resolve any query or concern you bring to us regarding our use of your information. But if you feel we haven’t adequately addressed your issues, you have the right to lodge a complaint with a supervisory authority or regulator in your jurisdiction. For example, EU users can contact their country’s Data Protection Authority (DPA), UK users can contact the Information Commissioner’s Office (ICO), Canadian users can reach the Office of the Privacy Commissioner (OPC), Australian users can contact the Office of the Australian Information Commissioner (OAIC), Brazilian users can reach out to the ANPD, and Mexican users can contact the INAI. We provide specific contact info for some of these in the jurisdiction sections. We would, of course, appreciate the chance to address your concerns first, so do consider reaching out to us, but the choice is yours and your rights with regulators are respected.

        </p>
        <p>
          <b>
Opt-Outs and Preferences:
          </b> Apart from formal rights, we offer various user controls:

        </p>

        <ul>
        <li>
          <b>
          Email/Text Preferences:
          </b> You can unsubscribe from marketing emails any time by using the unsubscribe link or contacting us. Transactional emails (important account or service messages) may still be sent as they are not promotional.

        </li>
        <li>
          <b>
Cookie Controls: 
          </b> use browser settings to manage cookies.

        </li>
        <li>
          <b>
In-App Settings:
          </b> If our app collects certain data like usage analytics or crash reports, we may provide an in-app toggle to turn off sending those diagnostics (if not required for functionality). Check the app settings or privacy menu.

        </li>
        <li>
          <b>
Push Notifications:
          </b> With your consent, we may send push notifications for reminders or updates. If you no longer want those, you can disable PawPlate notifications in your device settings.

        </li>
        <li>
          <b>
Third-Party Ad Preferences:
          </b> While we don’t share data for ads, if you interact with any third-party links, remember you can often opt out of targeted ads via tools like the Digital Advertising Alliance’s opt-out or platform settings as relevant.

        </li>
        </ul>

      <p>
        To summarize this section: It’s your data and we want you to feel comfortable and empowered regarding its use. We’re here to facilitate your rights in a timely and respectful manner. For specifics pertaining to your region (which might grant additional rights or details), please see the next section.
      </p>

         <h3 className="terms-heading" id="security">Data Security and Breach Response</h3>
         <p>
          We employ a variety of security measures to safeguard your personal information against unauthorized access, alteration, disclosure, or destruction. While no system is 100% foolproof, we are committed to keeping your data safe. In the unfortunate event of a data breach, we have procedures in place to document the incident, mitigate any harm, and notify affected parties and regulators as required by law. Transparency and prompt action are key to our approach.

         </p>
         <p>
          <b>Security measures:</b> PawPlate takes security seriously from design to deployment:

         </p>
         <ul>
          <li>
            <b>
          Encryption:
            </b> We use encryption to protect data in transit and at rest.

          </li>
          <li>
            <b>
              Access Controls:
            </b>  We limit access to personal data to only those employees, contractors, and service providers who need it to perform their work. They access it under strict confidentiality obligations. Our team is trained on data protection. We use authentication controls (strong passwords, 2-factor authentication where possible) for our systems to prevent unauthorized internal access.

          </li>
          <li>
            <b>
              Network and System Security: 
            </b> Our servers are protected by firewalls and monitoring systems to guard against intrusions. We regularly update our software and systems to patch vulnerabilities. We employ anti-malware tools and perform security testing (including possibly periodic penetration tests or code reviews) to identify and fix weaknesses.

          </li>
          <li>
            <b>
              Data Minimization:
            </b> By collecting only what we need and retaining it only as long as needed, we reduce the amount of information that could be compromised in the unlikely event of a breach. Less data retained = less data at risk.

          </li>
          <li>
            <b>
              Anon/Pseudo:
            </b> Where feasible, we pseudonymize or anonymize data for certain analytic uses, meaning the data cannot be easily traced back to an individual without additional information that is kept separate.

          </li>
          <li>
            <b>
              Physical Security:
            </b> If any data is stored in physical form or on physical servers, those are in secure facilities with access controls. Our cloud providers also maintain high physical security at their data centers.

          </li>
          <li>
            <b>
              Testing and Auditing:
            </b> We continuously monitor for potential threats and we maintain logs of access to data. Suspicious activities trigger alerts for our security team to investigate. We may also use third-party security services to help detect anomalies.

          </li>
         </ul>
         <p>
          <b>
Payment Security: 
          </b> Financial information is particularly sensitive. As mentioned, we use trusted third-party payment processors who are compliant with security standards like PCI-DSS. Our app or site will not store your full payment card details; they are handled by the payment gateway (for example, Stripe, PayPal, etc.). Those companies specialize in secure payment processing. We receive a transaction ID or token and maybe the last 4 digits of your card for reference, but not the full card data after the transaction. This approach ensures that even if our database were compromised, your full financial information wouldn’t be in it.

         </p>
         <p>
          <b>
            User Responsibilities:
          </b> Despite our measures, security also depends on you. We urge you to choose a strong password and keep it secret. Don’t share your account credentials with others. Use unique passwords for different services. If you suspect any unauthorized access to your account or any security issue, please notify us immediately at <a href="mailto:support@pawplate.io">support@pawplate.io</a>. We’ll work with you to secure the account (like resetting credentials). Also, keep your devices secure and updated, as device-level malware can undermine any app’s security.

         </p>
         <p>
          <b>
            Incident Response Plan:
          </b> We have a plan in place for how to handle a personal data breach (as defined by applicable law). A “data breach” could be things like accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to personal data. Our plan includes:

         </p>
         <ul>
          <li>
            <b>
          Investigation:
            </b> Upon discovering or suspecting a breach, we will immediately assemble a response team to investigate the scope, cause, and data involved. We document everything we find. We work to stop the breach (e.g. shut down intrusions, patch vulnerabilities) and secure our systems.

          </li>
          <li>
            <b>
              Risk Assessment: 
            </b> We will determine the risk level of the breach to individuals’ rights and freedoms. Not all breaches require notification; for example, if data was encrypted and not usable by an attacker, risk might be low. But if personal data was exposed and could lead to harm (identity theft, financial loss, etc.), that’s a high risk scenario.
          </li>
         
          <li>
            <b>
              Authorities Notification: 
            </b> If the breach meets certain thresholds, we will notify the relevant authorities. Under GDPR, if a breach is likely to result in a risk to individuals, we must notify the Data Protection Authority within 72 hours of becoming aware.

          </li>
          <li>
            <b>
              Individual Notification:
            </b> If a data breach is likely to result in a high risk to your rights (for example, risk of fraud or personal harm), we will notify you without undue delay. That means as soon as we have the core facts and have implemented initial containment, we’ll reach out via appropriate channels to inform you of the breach. We will tell you what happened in general terms, what data is involved, what we are doing about it, and what you can do to protect yourself (like changing passwords, monitoring accounts, etc.). Under some laws, individual notice can be skipped if we’ve taken measures that eliminate the risk (for example, if the data was encrypted and unusable , or we’ve taken immediate steps to prevent harm).

          </li>
          <li>
            <b>
              Remediation and Follow-up:
            </b> After containment, we will work to remediate the issue that caused the breach, to prevent similar events in the future. We update our procedures based on lessons learned. We also might provide assistance to affected users.

          </li>
          <li>
            <b>
              Documentation: 
            </b> We maintain an internal breach register documenting all incidents, even small ones. This is both a GDPR requirement and a good practice to track patterns and responses. Regulators can ask to see this log. It includes details like date, circumstances, effects, and actions taken for each incident.
          </li>
         </ul>
         <p>
          We hope never to have to notify you of a breach, but if we do, we want you to trust that we’ll handle it professionally and put your interests first. If you have any questions about security, feel free to reach out. 
         </p>

        <h3 className="terms-heading" id="children">Children’s Privacy</h3>
        <p>
          PawPlate Service is not intended for children under the age of 13. We do not knowingly collect personal information from anyone under 13 years old. Our platform and content are directed at adults (or at least at pet owners who are typically responsible teens/adults). If you are under 13, please do not use PawPlate Service or submit any information to us.

        </p>
        <p>
          If we learn that we have inadvertently collected personal information from a child under 13, we will delete that information as quickly as possible. If you are a parent or guardian and you become aware that your young child has provided us with personal data, please contact us immediately so we can take appropriate action.

        </p>
        <p>
          For teens between 13 and 18 (or the age of majority in your jurisdiction): If you are a minor but old enough to use online services with parental consent, you should only use PawPlate Service with the involvement of a parent or guardian. Some jurisdictions have stricter age requirements (for example, under 16 in the EU for certain data processing consent). We comply with those laws – if consent is required from a parent/guardian for us to lawfully process a minor’s data, we will not process that data without such consent.

        </p>
        <h3 className="terms-heading" id="changes">Changes to this Policy</h3>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or for other operational reasons. If we make changes, we will notify you by revising the “Last Updated” date at the top of this Policy. In case of material changes (for example, if we start collecting new types of personal data or introduce new purposes not originally disclosed, or if we change how we share data in a significant way), we will take additional steps to inform you:

        </p>
        <ul>
          <li>
            <b>
            Notification:
            </b>  We might provide a prominent notice in the app or on our website (e.g. a banner or pop-up) and/or send you an email notification to the address associated with your account, explaining the changes.

          </li>
          <li>
            <b>
              Consent if required: 
            </b> If a change involves something that requires your consent by law (for instance, using your data for a new purpose that you originally didn’t agree to), we will obtain your consent before implementing that change with respect to your information. Otherwise, the changes will apply to you from the effective date of the updated Policy.

          </li>
        </ul>
        <p>
          We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information. If you continue to use PawPlate after the updated Policy is in effect, it will signify acceptance of the changes, to the extent permitted by law. However, we will not reduce your rights under this Privacy Policy without your explicit consent. If you do not agree with any changes, you should discontinue use of PawPlate Service and may request us to delete your data.

        </p>
       <h3 className="terms-heading" id="contact">Contact Us</h3>
       <p>
        We welcome your questions, concerns, and feedback about privacy. If you need to contact us or exercise any of your rights as described above, you can reach us in the following ways:

       </p>
       <ul>
        <li>
          <b>
          Email:
          </b> You can email us at <a href="mailto:support@pawplate.io">support@pawplate.io</a>. This is the fastest way to reach us with privacy-related inquiries, including requests to access or delete your data.
        </li>
        <li>
          <b>
            Postal Mail: 
          </b> If you prefer, you may write to us at: 1111B S Governors Ave STE 25056, Dover, DE 19904, United States.

        </li>
       </ul>
       <p>(If you are exercising a specific legal right via mail, please provide contact info for us to reach you and describe your request clearly.)
</p>

<p>
  We will respond to legitimate requests or inquiries as soon as reasonably possible, and certainly within any timeframes required by law. If you are contacting us to exercise rights, please be prepared to undergo our verification process as described earlier, so we can safeguard against unauthorized requests.

</p>
    <p>
      <b> 
    Language:
      </b> We can correspond in English. If you require communication in another language, we will do our best to accommodate.

    </p>
     <p>
      <b> 
        Regulator Contacts:
      </b> As noted, you have the right to reach out to privacy regulators. We provide some useful contacts here for convenience:

    </p>
    <ul>
      <li>
EU: A list of Data Protection Authorities is available on the European Data Protection Board’s website. For example, in France it’s CNIL, in Germany each state has one, in Ireland the Data Protection Commission, etc.

      </li>
      <li>
        UK: Information Commissioner’s Office (ICO) – website: ico.org.uk, tel: +44 303 123 1113.

      </li>
      <li>
        California: California Privacy Protection Agency (CPPA) – website: cppa.ca.gov  (though for individual complaints, you might go through the Attorney General’s office until CPPA sets up a mechanism).

      </li>
      <li>
        Canada: Office of the Privacy Commissioner (OPC) – website: priv.gc.ca, email: ic.info.privacy-privacy.info.ic@canada.ca (or use their online form).

      </li>
      <li>
        Australia: Office of the Australian Information Commissioner (OAIC) – website: oaic.gov.au, tel: +61 1300 363 992.

      </li>
      <li>
        Brazil: National Data Protection Authority (ANPD) – website: www.gov.br/anpd, email form on site.

      </li>
      <li>
        Mexico: National Institute of Transparency, Access to Information and Personal Data Protection (INAI) – website: home.inai.org.mx, tel: +52 800 835 4324.

      </li>
    </ul>
<p>
  We hope you never need to escalate a complaint, but we provide these contacts in the spirit of full transparency about your rights.

</p>
<p>
  <b>
    Thank you for taking the time to read our Privacy Policy. Your trust is important to us, and we are continuously working to maintain and deserve that trust through strong privacy and security practices.

  </b>
</p>
      <h3 className="terms-heading" id="jurisdiction">Jurisdiction-Specific Notices</h3>
      <p>
        The following section provides additional information for residents of certain jurisdictions, as required by those jurisdictions’ privacy laws. These supplements clarify how we comply with specific local laws and the rights of residents in those areas. Please refer to the section(s) relevant to your location:
      </p>
      <p>
        <b>
          European Union / European Economic Area and United Kingdom 
        </b>
      </p>
      <p>
        If you are in the European Union (EU) or European Economic Area (EEA), or in the United Kingdom (UK), the following provisions apply in addition to (and, where applicable, supersede) the rest of the Privacy Policy: 

      </p>
      <p>
       <b> Controller:</b> For EU/EEA/UK users, the “data controller” of your personal data is Opami LLC (1111B S Governors Ave STE 25056, Dover, DE, 19904, United States). 

      </p>
     <b> Legal Bases for Processing:</b> We process your personal data only if we have a valid legal basis under Article 6 of the GDPR/UK GDPR. Here is a summary of our legal bases for the key processing activities (as also outlined earlier): 
      <ul>
        <li>
          <b>
    Performance of a Contract (Art. 6(1)(b)):
          </b> Most of our data use is to provide you with the PawPlate Services you requested. When we use your data to set up your account, deliver feeding recommendations, process orders/payments, or otherwise fulfill our contract with you, this is our legal basis. If you choose not to provide data necessary for the service (e.g. account login or pet info), we may not be able to perform the contract. 

        </li>
        <li>
          <b>
            Consent (Art. 6(1)(a)):
          </b> We rely on your consent for certain processing that is voluntary. This includes sending marketing communications (where required by law), using non-essential cookies or similar technologies on the website, or processing any special categories of data (though we generally avoid collecting special category data about you – pet data is not a special category about you). Whenever we rely on consent, you have the right to withdraw it at any time (which will not affect processing done before withdrawal). For example, if we ever collect your explicit consent to use a testimonial with your name, you can later ask us to stop using it, and we will. 

        </li>
        <li>
          <b>
            Legitimate Interests (Art. 6(1)(f)):
          </b> We process some data for purposes that are in our legitimate interests, which we’ve balanced against your privacy rights. Our legitimate interests include: improving and securing our products and services; understanding how users engage with PawPlate Service; communicating with you about product updates or pet care advice (in a context where consent is not strictly required); fraud detection and prevention; and potential corporate transactions (due diligence, etc.). When we rely on this basis, we ensure our interests are not overridden by your interests or fundamental rights. You have the right to object to processing based on legitimate interests (see above Rights section). 

        </li>
        <li>
          <b>
             Legal Obligation (Art. 6(1)(c)):
          </b>  When processing is necessary for us to comply with a law, this is our basis. For instance, retaining transaction records for tax, or providing information to authorities as required by law, or obligations under GDPR itself (like responding to your exercise of rights). 

        </li>
        <li>
          <b>
            Other Bases:
          </b> In rare cases we might rely on other bases like Vital Interests (Art.6(1)(d)). Primarily, the above four are what we use. 

        </li>
      </ul>
      <p>
        <b>
          We also note: We generally do not process any “special categories” of personal data about individuals in the EU (like personal health data, biometric data, etc.). 

        </b>
      </p>
      <p>
        <b>
          Data Subject Rights (EU/UK): Your rights under GDPR and UK GDPR include the right to access, rectification, erasure, restriction, objection, and data portability, as described in Your Privacy Rights and Choices. You also have the right <b> not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects concerning you or similarly significantly affects you  </b> (GDPR Art. 22). As explained, PawPlate does not engage in such processing without your consent or another lawful basis, and our automated profiling (feeding suggestions) does not have significant effects on you. If you believe it does or have concerns, you can request human intervention or to express your point of view, and we will review any such decision.

        </b>
      </p>
      <p>
        <b>
        Right to Complain: 
        </b> If you are in the EU/EEA, you have the right to lodge a complaint with a supervisory authority in the Member State of your habitual residence, place of work, or place of the alleged infringement. For example, if you’re in Germany, you can contact your state’s DPA; in France, CNIL; in Ireland, the DPC, etc. If you’re in the UK, you can lodge a complaint with the Information Commissioner’s Office (ICO). We have provided links in Contact Us or you can easily find your authority online. We would appreciate if you try to resolve with us first, but it’s your right to go to the authority at any time. 

      </p>
       <p>
        <b>
          International Transfers (EU/UK specific):   
        </b> As detailed earlier, we transfer data to the EU and the U.S. and possibly other countries. The European Commission has not granted the U.S. an unqualified adequacy decision. PawPlate ensures appropriate safeguards through <b>Standard Contractual Clauses (SCCs)</b> for transfers to our U.S. operations and any sub-processors. We also have executed the UK’s International Data Transfer Addendum for UK data as needed. We monitor legal developments (e.g., the status of the EU-U.S. Data Privacy Framework and UK-U.S. discussions). 
      </p>
       <p>
        <b>
          Necessity of Data Provision:
        </b> Under GDPR, we inform you whether providing personal data is part of a statutory or contractual requirement, and the consequences of not providing it. When you sign up for PawPlate Service, providing certain data is necessary to enter into the contract (like at least an email and password for an account, and some pet info to use the core features). If you don’t provide those, we can’t create an account or deliver the service. Other data is optional – not providing optional data might mean you can’t use some feature, but it’s not blocking your general use. We’ll indicate required fields vs optional in the app. 

      </p> 
      <p>
        <b>
          Direct Marketing (EU/UK):
        </b> We will only send you promotional emails or texts if you have either (a) opted in, or (b) if you are an existing customer, we may send you info about similar products and you haven’t opted out (the “soft opt-in” under some national laws). In any event, we always provide a no-cost way to opt out and we abide by that choice. We do not share your info with third parties for their direct marketing unless you explicitly agree. 

      </p>
      <p>
        <b>
          Data Protection by Design and Default: We implement the principles of GDPR Article 25. This means we consider privacy at the initial design phase of our product and by default we ensure that only necessary data is processed. We also conduct, when appropriate, Data Protection Impact Assessments (DPIAs) for higher-risk processing activities to identify and mitigate risks to your rights. 

        </b>
      </p>
      <h3 className="terms-heading">California (CCPA/CPRA) </h3>
      <p>
        If you are a resident of California, you have specific privacy rights under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA). The following disclosures are made pursuant to the CCPA/CPRA and related regulations: 

      </p>
      <p>
        <b>
          Categories of Personal Information Collected: 

        </b> Examples: real name, email address, unique user ID, IP address, and device identifiers. We collect these from you (e.g., when you create an account, enter your email) and automatically (IP and device IDs when you use the service). 

      </p>
      <ul>
        <li>
          <b>
             Identifiers (Category A):
          </b> Examples: real name, email address, unique user ID, IP address, and device identifiers. We collect these from you (e.g., when you create an account, enter your email) and automatically (IP and device IDs when you use the service). 

        </li>
        <li>
          <b>
            Customer Records (Category B): 
          </b>  This includes personal information described in the California customer records law, such as payment information. We collect payment data through our secure payment processor when you conduct transactions. 

        </li>
        <li>
          <b>
            Protected Classification Characteristics (Category C):
          </b> We do not intentionally collect characteristics of protected classifications (like race, gender, date of birth)
about our users in providing our service. We might indirectly infer your general region from IP (which could correlate with language), but we don’t ask for protected info. (Note: Info about your pet’s breed or health is not a protected characteristic about a person.) 

        </li>
        <li>
          <b>
            Commercial Information (Category D):
          </b> This includes records of products or services purchased or considered. We collect/maintain your subscriptions, and related details as you use PawPlate Service. 

        </li>
        <li>
          <b>
            Internet or Other Electronic Network Activity (Category F):
          </b> We collect browsing/activity data such as your interactions with our website/app, logs of usage, cookie data, etc. This covers analytic information about how you use PawPlate (pages visited, features used, etc.), as described earlier. 

        </li>
        <li>
          <b>
            Geolocation Data (Category G):
          </b> We do not collect precise geolocation (exact GPS). We may have approximate location information from your IP address (e.g., IP might show city or region). 

        </li>
        <li>
          <b>
            Sensory Data (Category H):
          </b>  We do not collect audio, electronic, visual, thermal, olfactory, or similar sensory information in the normal course of our service. (For example, we’re not recording phone calls or using your camera.) 

        </li>
        <li>
          <b>
            Professional or Employment Information (Category I):
          </b> Not collected. We don’t ask for your job title, employer, or education in our consumer app context.
        </li>
        <li>
          <b>
            Education Information (Category J):
          </b> Not applicable. We don’t collect any info that is considered education records or related info under this category. 

        </li>
        <li>
          <b>
            Inferences (Category K)
          </b> We do draw inferences from the information we have to personalize your experience. For example, from your pet data and usage we infer preferences to recommend. These inferences feed into your personalized recommendations and what content you see in the app. We do not create profiles about you for broader advertising purposes outside of PawPlate’s Service context. 


        </li>
      </ul>
      <p>
        <b>
          Purpose of Collection:
        </b> We collect these categories of information for the business and commercial purposes described in How We Use Your Information. In summary, the purposes include: providing the service (account, recommendations, transactions), improving the service (analytics, research), communicating with you, ensuring security, processing payments, and marketing PawPlate’s offerings to you (with your preferences honored). We do not use your personal info for purposes materially different from those in this Policy without notifying you. 

      </p>
      <p>
        <b>
          Sources of Personal Information:
        </b>  As outlined earlier, we obtain this information: 

      </p>
      <ul>
        <li>
          Directly from you (e.g., when you register, input data, communicate with us). ● Automatically through your use of our services (e.g., via cookies or app usage). ● From service providers or third parties (e.g., a payment processor confirming a transaction, or if you sign in via a third-party service which provides some info). 

        </li>
      </ul>
      <p>
        We have not sold personal information (including not selling minors’ info). “Sold” in CCPA means we haven’t exchanged personal data for monetary or other valuable consideration with third parties for their own use. Likewise, we have not shared personal information for cross-context behavioral advertising purposes (CPRA’s definition of “share”). We don’t provide your data to ad networks to target you on other sites. Therefore, we did not include a “Do Not Sell or Share My Personal Information” link, because we don’t engage in selling/sharing as defined. 

      </p>
      <p>
        <b>
          Sensitive Personal Information:
        </b> Under CPRA, certain data is classified as Sensitive Personal Information (SPI). We do not collect most types of SPI about consumers. The one piece that might be considered “sensitive” is account login credentials (email + password) since compromise of that could grant account access – we protect those heavily (hashing passwords, etc.). Payment card details in combination with security code could be sensitive,
but we don’t store full card numbers or codes ourselves. Therefore, the CPRA right to “Limit the Use and Disclosure of Sensitive Personal Information” does not apply to our handling, because we only use the minimal quasi-sensitive info (login credentials) for providing the service and security – which is considered a permitted purpose (not requiring offering a limit opt-out). 

      </p>
      <p>
        <b>
          California Consumer Rights:
        </b>  If you are a California resident, you have the following rights under CCPA/CPRA: 

      </p>
      <ul>
        <li>
          <b>
           Right to Know: 
          </b> You can request that we disclose to you (a) the categories of personal information we have collected about you, the categories of sources, the business or commercial purposes for collection, the categories of third parties to whom we disclosed that info, and the categories of info sold or shared (we would say none in our case); and (b) the specific pieces of personal information we have collected about you. The categories part is largely covered in this notice itself. The specific pieces would be, for example, an export of your actual data (which overlaps with access right). Do note, we are obligated to provide this info for the 12-month period preceding your request (and in some cases can provide beyond 12 months if requested and feasible, since CPRA allows longer lookback now). 

        </li>
         <li>
          <b>
            Right to Delete:
          </b> You can request deletion of personal information we have collected from you, subject to certain exceptions (if it’s necessary for us or our service providers to maintain the info for reasons allowed by law, we may deny deletion for those specific pieces). Common exceptions: completing the transaction or service requested, detecting security incidents, legal compliance (like retaining transaction records), debugging, or for lawful internal uses aligned with the relationship (as defined in CCPA 1798.105(d)). If we deny a deletion request in part (e.g. we keep transaction data for legal reasons), we will inform you. 

        </li>
         <li>
          <b>
            Right to Correct:
          </b> If we maintain inaccurate personal information about you, you can request we correct it. We will take into account the nature of the info and purpose of processing, and make commercially reasonable efforts to correct as directed by you and necessary. 

        </li>
         <li>
          <b>
            Right to Opt-Out of Sale/Sharing:
          </b> As noted, we don’t sell or share your info as defined, so this right is moot. 

        </li>
        <li>
          <b>
Right to Limit Use of Sensitive PI:
          </b> Also as noted, we do not use sensitive personal info in a way that triggers the right to limit (we only use what little sensitive-equivalent info we have for providing the service, e.g. account auth). 

        </li>
         <li>
          <b>
            Right to No Retaliation:
          </b> We will not discriminate or retaliate against you for exercising any CCPA rights. This means no denial of goods/services, no change in price or quality of service, no suggesting you will get a different price or quality for using your rights. 

        </li>
      </ul>
    <p>
      <b>
        Submitting Requests (California):
      </b>  You or your authorized agent can submit requests through the contact methods in Contact Us. 

    </p>
    <p>
      For a Right to Know (categories or specific pieces) request or Deletion request, the law may require a more stringent verification (e.g., two data points or even three data points match). We will inform you if we need more info for verification. For a Correction request, we will verify identity similarly and then work with you to identify the inaccurate info and correct it. 

    </p>
    <p>
      We aim to respond within <b>45 days</b> of receiving your verifiable request. If we need more time (up to another 45 days, totaling 90), we’ll let you know within the first 45 days and explain why. Our response will cover the information requested or the action taken (deletion/correction) and any explanation if we couldn’t comply with some part of the request.
For access requests, we’ll either provide the info electronically (usually via a secure email) or by mail if you prefer. 

    </p>
    <p>
      We do not charge a fee for handling your requests unless they are excessive or manifestly unfounded/repetitive, in which case we are allowed to charge a reasonable fee or refuse, but we would provide an explanation. We haven’t had to do that and hope we won’t. 

    </p>
    <p>
      <b>Shine the Light Law:</b> Separately from CCPA, California’s “Shine the Light” law (Civil Code §1798.83) allows customers to ask for certain information about any disclosure of personal info to third parties for their direct marketing purposes in the preceding year. We do not disclose personal info to third parties for their own direct marketing, so this law is not particularly applicable. If you ask, we can confirm that. 

    </p>
    <p>
      <b>
        Categorical Disclosure Chart (for transparency): 

      </b>
    </p>
    <p>
      For those who prefer a chart format for CCPA:

    </p>
    <img className="terms-wrapper-img" src={tb} alt="legal"/>
    <p>

    </p>
     <img className="terms-wrapper-img" src={tb1} alt="legal"/>
     <p>

     </p>
      <img className="terms-wrapper-img" src={tb2} alt="legal"/>
      <p>
        This chart is provided for compliance and ease of reference for California consumers. 
      </p>
      <h3 className="terms-heading">Canada (PIPEDA) </h3>
      <p>
        If you are in Canada, the Personal Information Protection and Electronic Documents Act (PIPEDA) and similar provincial laws (where applicable, e.g. Alberta, B.C., Quebec’s new Law 25, etc.) apply. We process personal information in accordance with the fair information principles in these laws: 
      </p>
      <ul>
        <li>
          <b>
           Accountability: 
          </b> We have designated our Privacy Officer (contact in Contact Us) to be accountable for our compliance with Canadian privacy laws. We are responsible for personal information under our control, including information we may transfer to service providers (we ensure they provide comparable protection via contracts).
        </li>
        <li>
          <b>
            Identifying Purposes:
          </b> We have explained the purposes for which we collect personal information in How We Use Your Information. Generally, we collect and use data to provide and improve the PawPlate service, as described. If we ever intend to use your data for a new purpose not identified, we will seek your consent (unless it’s an exception like legal compliance). 

        </li>
        <li>
          <b>
            Consent:
          </b> We rely on your consent for the collection, use, and disclosure of your personal information, except where otherwise permitted by law. By using our service and providing personal info, you imply consent for the identified purposes. For any secondary or optional uses (like receiving marketing communications), we obtain express consent (e.g., by having you opt in). You have the right to withdraw consent at any time (subject to legal or contractual restrictions – we’ll inform you if any apply, and consequences of withdrawal like not being able to provide some services). For example, you can withdraw consent to marketing emails by unsubscribing. If you withdraw consent for us to collect/use necessary info (like your profile data), we might have to deactivate your service as we cannot operate without it – but we will inform you and let you make that choice.

        </li>
        <li>
          <b>
            Limiting Collection:
          </b> We collect only the information necessary for the purposes we’ve identified. Our Policy details the limited nature of data we collect. We do not collect indiscriminately. 

        </li>
        <li>
          <b>
            Limiting Use, Disclosure, and Retention:
          </b>  We do not use or disclose personal info for purposes other than those for which it was collected, except with consent or as required by law. Disclosures to service providers are outlined (they’re essentially extensions of us). We retain info as described in our Data Retention section – only as long as needed for purposes or as required by law, after which it is destroyed or anonymized. If we transfer data across borders, it’s still for the same purposes and we ensure protection (Note: Canadian data may be subject to foreign laws, e.g., accessible to U.S. authorities under lawful processes – this is a standard PIPEDA cross-border disclosure; by using our service, you consent to such foreign transfer). 

        </li>
        <li>
          <b>
            Accuracy:
          </b> We strive to keep your information accurate and up-to-date. You can update your data through your account settings or by contacting us (right of rectification). We encourage you to correct any inaccuracies. We will update our records when notified or when we become aware of outdated info to minimize the possibility of using incorrect data. 

        </li>
        <li>
          <b>
            Safeguards:
          </b> We protect personal information with appropriate security safeguards given the sensitivity of the info. Our security measures (encryption, etc.) have been described in Data Security. This includes protecting data against loss or theft, as well as unauthorized access, disclosure, copying, use, or modification. Our employees are trained to respect confidentiality of personal info. 

        </li>
        <li>
          <b>
            Openness:
          </b> We maintain an open Privacy Policy (this document) describing our practices. You can contact us for any questions about our privacy practices, and we will provide further detail or explanation as needed. 

        </li>
        <li>
          <b>
            Individual Access:
          </b> You have the right to access your personal information under our control, with some exceptions as allowed by law (e.g., we might not be able to give access to info that reveals personal data about another individual, proprietary business info, or is subject to legal privilege, etc.). Normally, we will inform you of the existence, use, and disclosure of your personal info upon request, and give you access within a reasonable time (generally within 30 days as per PIPEDA’s guideline). We won’t charge a fee for a simple access request; if for some reason a request is extensive, we’ll advise if a minimal fee applies (rare). If you find any inaccuracies, you may request corrections and we will amend the information as appropriate and send corrections to any third parties as needed. If we refuse an access or correction request, we will provide a reason (e.g., if it falls under an exception) and inform you of any recourse. 

        </li>
         <li>
          <b>
            Challenging Compliance: 
          </b> If you have a complaint about our compliance with Canadian privacy laws, you can contact us to discuss your concerns. We will investigate and respond to your complaint. If you are not satisfied with our response, you have the right to bring your complaint to the Office of the Privacy Commissioner of Canada (OPC) or relevant provincial Privacy Commissioner (like in Quebec, Alberta, or B.C. if applicable). We will provide you with information on how to do so if needed. 

        </li>

      </ul>
      <p>
        <b>
          Cross-Border Transfers:
        </b> As mentioned, your data may be processed in the U.S. or other countries by us or our service providers. These jurisdictions may have different data protection rules than Canada. In the event of foreign storage, data may be accessible to law enforcement or national security authorities of that jurisdiction. By using PawPlate Service, you consent to such transfer. We ensure that foreign service providers are bound to confidentiality and we only engage reputable companies. If you wish to know more about our practices or have questions about overseas processing, contact us. 

      </p>
      <h3 className="terms-heading">Australia (APPs)</h3>
     
      <p>
        If you are in Australia, we handle your personal information in accordance with the Australian Privacy Act 1988 and the 13 Australian Privacy Principles (APPs): 

      </p>
      <ul>
        <li>
          <b>
APP 1 – Open and Transparent Management: 
          </b> This Policy outlines how we manage personal info. You can contact us for any additional info. We are committed to transparency about our privacy practices. 

        </li>
        <li>
          <b>
            APP 2 – Anonymity and Pseudonymity: 
          </b> Where practicable, you have the option of not identifying yourself or using a pseudonym when dealing with us. However, for many aspects of our service (like an account), it’s not practicable to provide it anonymously because we need certain details (e.g., an email to log in or send notifications). That said, you could choose to use a nickname for your profile if we allow (except where real name is needed for purchases). If you just browse our website informationally, you don’t have to give your name. 

        </li>
        <li>
          <b>
            APP 3 – Collection of Solicited Information:
          </b>  We only collect personal info that is reasonably necessary for our functions. We have described the types of info we collect and purposes. We do not collect sensitive info (as defined in Aus law: health info about an individual, etc.) without consent. As noted, we don’t collect your own health info, just pet info (not considered “sensitive personal information” about you under the Privacy Act). 

        </li>
        <li>
          <b>
            APP 4 – Unsolicited Information:
          </b>  If we receive personal info that we didn’t solicit (like someone emails us a third party’s info unexpectedly), we will determine if we could have collected it under APP 3. If not, we will destroy or de-identify it if lawful and reasonable to do so. 

        </li>
        <li>
          <b>
            APP 5 – Notification of Collection:
          </b> We take steps to notify you at or before the time of collection (or as soon as practicable after) about things like our identity and contact, the fact of collection, purposes, consequences if any if not collected, usual disclosures, and this Privacy Policy. This Policy serves as a comprehensive notice. When you sign up, we provide a link to this Policy. If we ever collect via other means, we would similarly inform you of these matters. 

        </li>
        <li>
          <b>
            APP 6 – Use or Disclosure:
          </b> We only use or disclose personal info for the purposes for which it was collected, or for related purposes you’d expect, or as otherwise permitted by law (like with consent or certain exceptions). The ways we use/disclose are detailed earlier. If we need to use or disclose info for any other purpose, we will get your consent unless an exception applies (e.g., required by law or necessary in an emergency). 

        </li>
        <li>
          <b>
            APP 7 – Direct Marketing:
          </b> We will not use or disclose your personal info for direct marketing purposes unless allowed by APP 7. In practical terms, that means we will only send marketing communications (emails about offers, etc.) consistent with your reasonable expectations and with your consent when required. As a customer, you might reasonably expect to get some product updates. However, if you opted out or did not consent where consent is needed, we won’t send. Every marketing email will have an opt-out mechanism. We do not share your info with third parties for their direct marketing. You can opt out of our marketing at any time, and we will process that request quickly. 

        </li>
        <li>
          <b>
            APP 8 – Cross-border Disclosure:
          </b> Before disclosing personal info to overseas recipients, we take steps to ensure they don’t breach the APPs in relation to that info. This is done via contractual agreement and due diligence on their practices (similar to what APP 8 requires). We either ensure the recipient is subject to a law or scheme substantially similar to the APPs or we get your consent if needed with awareness of cross-border risks. By using our service, you consent to your info being transferred internationally. Nonetheless, we remain accountable and take responsibility as needed under APP 8. (Noting that if an exception in APP 8.2 applies – e.g., necessary for our contract with you – we can transfer; but we still ensure protection of your data.)

        </li>
        <li>
          <b>
            APP 9 – Government Identifiers:
          </b> We do not use any government identifiers (like passport numbers, Medicare, driver’s license) to identify you in our service. We simply don’t collect those. 

        </li>
        <li>
          <b>
            APP 10 – Quality of personal information: 
          </b> We take reasonable steps to ensure the personal info we collect is accurate, up-to-date, and complete. This includes allowing you to correct your data (and encouraging updates). We also ensure that when using or disclosing info, it’s relevant and not misleading. For instance, if you update an email, we use the updated one to contact you, not an old one. 

        </li>
        <li>
          <b>
            APP 11 – Security of personal information:
          </b> We take steps to protect personal info from misuse, interference, and loss, as well as unauthorized access, modification, or disclosure – as described in Data Security and Breach Response. We also have processes to destroy or de-identify personal info when it’s no longer needed (and when allowed by law). For example, we securely delete data after retention periods. 
        </li>
        <li>
          <b>
            APP 12 – Access to personal information:
          </b> You have the right to request access to the personal information we hold about you. Our process: if you request, we will give you access within a reasonable period (typically 30 days) in the manner you request if it’s reasonable (for example, email you a copy). There are some exceptions under which we may refuse access. If we refuse (in whole or part), we’ll give you written notice with reasons (unless unreasonable to do so) and how to complain. Generally, we aim to provide access. We might require proof of identity. We typically do not charge an access fee, but if the request is complex, we may charge a reasonable cost for providing access, but not for making the request itself. We’ll discuss any fee with you beforehand if that’s the case. 

        </li>
        <li>
          <b>
            APP 13 – Correction of personal information:
          </b> If you believe the personal information we hold about you is inaccurate, out-of-date, incomplete, irrelevant, or misleading, you can request correction. If we agree, we will correct it and inform you. If we do not agree that a correction is required, we’ll let you know our reason (in writing, if you request) and the process to escalate (like making a complaint). Additionally, if you request, we will take reasonable steps to notify any third party that we disclosed the incorrect info to, about the correction (unless it is impractical or unlawful to do so). Even without a request, if we identify information that is wrong, we will correct it. We won’t charge for correcting info. 

        </li>
      </ul>
      <p>
        <b>Complaints (Australia):</b>  If you have a privacy concern or believe we’ve breached the APPs in handling your personal info, please contact us with details of your complaint. We will acknowledge your complaint and aim to investigate and respond within a reasonable time (usually 30 days). We’ll seek to resolve it with you. If you are not satisfied with our response, you can lodge a complaint with the Office of the Australian Information Commissioner (OAIC). We hope to work it out directly, but you have that option. 


      </p>
        <h3 className="terms-heading">Brazil (LGPD) </h3>
        <p>
          For users in Brazil, we abide by the Brazilian General Data Protection Law (Lei Geral de Proteção de Dados – LGPD). Key points under LGPD: 

        </p>
        <ul>
          <li>
            <b>
             Legal Bases (Bases Legais): 
            </b> LGPD provides several legal grounds for processing (Art. 7). We process your personal data primarily on the following bases:

            <ul>
              <li>
                <b>
         Consent (Seu consentimento livre, informado e inequívoco): 
                </b> For sending marketing emails or using optional data, we obtain your consent. You can revoke consent at any time (via channels provided), and we will stop that processing. We ensure consent is obtained in advance for any processing of personal data that requires it under LGPD. 

              </li>
              <li>
                <b>
                  Contract (Execução de contrato):
                </b> When processing is necessary to provide our services under the contract with you (e.g., using your info to deliver the
app functionality, fulfill orders, etc.), it falls under the contractual necessity basis. 

              </li>
              <li>
                <b>
                  Legitimate Interest (Interesse legítimo do controlador):
                </b> We use legitimate interests for some processing, like improving services, fraud prevention, etc., carefully assessing that this processing is expected by you and doesn’t infringe your rights. We have conducted Legitimate Interest Assessments in applicable cases. 

              </li>
              <li>
                <b>
                  Legal Obligation (Cumprimento de obrigação legal):
                </b> Some data we keep or share because applicable law requires us to, e.g., tax and accounting records, responding to judicial requests. 

              </li>
              <li>
                <b>
 Rights of Data Subjects (Direitos dos Titulares):
                </b> Under LGPD, you have several rights, largely in line with GDPR rights: 

              </li>
              <li>
                <b>
                  Confirmation of Processing (Confirmação):
                </b>  You can ask us to confirm whether we process your personal data . 

              </li>
              <li>
                <b>
                  Access: 
                </b>  You can request access to your data that we hold. 

              </li>
              <li>
                <b>
                  Correction (Retificação): 
                </b> Right to request correction of incomplete, inaccurate, or outdated data. 

              </li>
              <li>
                <b>
                  Anonymization, Blocking, or Deletion:
                </b> If your data is unnecessary, excessive, or processed in non-compliance with LGPD, you can request it be anonymized, blocked, or deleted. Essentially, if we shouldn’t have it or are using it wrongly, you can ask us to fix that. 

              </li>
              <li>
                <b>
                  Data Portability: 
                </b> Subject to ANPD regulations, you can request your data be ported to another service or supplier (this is similar to data portability right; as of now, ANPD guidelines on how to do this are developing, but we will comply once mechanisms are clear, likely by providing structured data as we do for others). 

              </li>
              <li>
                <b>
                  Deletion of Processed Data with Consent:
                </b> If we’re processing based on your consent, you can withdraw consent and request deletion of that data.
              </li>
              <li>
                <b>
                  Information about Sharing:
                </b> You can ask to whom your data has been shared (esp. public and private entities). 

              </li>
              <li>
                <b>
                  Information about Consent Possibility: 
                </b>  You have the right to know that you can or cannot provide consent and what are the consequences of denying consent. We’ve described in the Policy that some data is needed, and other is optional, etc. 

              </li>
              <li>
                <b>
                  Revoke Consent: 
                </b>  If processing is based on consent, you can revoke it at any time. 

              </li>
            </ul>
          </li>
          <li>
            To exercise these rights, contact us. We will respond as per LGPD’s timelines and requirements (generally within 15 days for most requests as per ANPD’s guidance). ● International Transfers: We transfer data internationally as described. Under LGPD, we ensure that such transfers are made in compliance with Chapter V of LGPD. ● Non-Discrimination: Similar to other laws, we will not refuse service or provide poor service if you exercise your LGPD rights. 

          </li>
          <li>
            <b>
ANPD Complaints:
            </b> You have the right to petition the Brazil’s ANPD about any issues in our data processing. You can do so after contacting us if you are not satisfied with our response, or directly if you prefer. Our hope is to resolve directly with you. The ANPD’s contact is on their site (mentioned in Contact Us). 

          </li>
           <li>
            <b>
               Data Breach Notifications:
            </b> In case of a security incident that may cause significant risk or damage to you, we will notify you and the ANPD in accordance with ANPD regulations (which suggest a very prompt notification, possibly within 2 business days of becoming aware, though the law says a “reasonable time”). We will follow whatever the current ANPD guidance is at the time, including content of notification and means. 

          </li>

        </ul>
         <h3 className="terms-heading">Mexico (LFPDPPP)</h3>
         <p>
          If you are in Mexico, the following applies per the Federal Law on Protection of Personal Data Held by Private Parties (Ley Federal de Protección de Datos Personales en Posesión de los Particulares) and its Regulations: 

         </p>
         <p>
          This section constitutes our “Aviso de Privacidad” for Mexican users: 

         </p>
         <p>
          <b>
Responsible Party:
          </b>  Opami LLC (1111B S Governors Ave STE 25056, Dover, DE, 19904, United States) is responsible (el Responsable) for the processing of your personal data. Our contact details for all privacy matters are provided in Contact Us. 

         </p>
         <p>
          <b>
            Personal Data Collected and Processed:
          </b> We collect the categories of personal data described earlier (identification data, contact data, financial data for payments, and information related to your pet usage, etc.). We do not collect data that Mexican law considers “sensitive personal data” (datos personales sensibles) about you (which would include things like your health, religion, biometric, etc.), nor do we collect your financial data beyond what’s needed for a transaction (and that we handle via secure processor). We also do not collect data of a nature requiring consent for use (like we are not handling your information in ways outside the exceptions provided by law). 

         </p>
         <p>
          <b>
            Primary Purposes:
          </b> We use your personal data for the primary purposes of: providing you the PawPlate service you requested (which includes creating your account, advising on pet nutrition, processing orders and payments, and providing customer support), maintaining and improving the quality of the service (analytics, product development), and ensuring security and functionality of the platform. These are necessary purposes that give rise to the legal relationship between you and PawPlate. 

         </p>
         <p>
          <b>
            Secondary Purposes:
          </b> We may also use your contact data for secondary purposes such as sending you promotional communications, offers, or newsletters related to PawPlate Service or pet care, and conducting surveys or market research to better understand user needs. We consider these secondary because they are not strictly necessary for the service. You have the right to object or refuse the processing of your data for these secondary purposes. If you prefer that we do not process your data for marketing or survey purposes, you can email us with your request (or use the unsubscribe links in communications). We will address your request and cease using your data for those purposes without affecting our provision of the main service to you. 

         </p>
         <p>
          By using our service and not objecting, you consent to the use of your data for the secondary purposes mentioned, but you can withdraw that consent at any time (we do not use an opt-in checkbox during sign-up for marketing; we provide a clear notice and an easy opt-out route, which is compliant with allowing you to exercise your right to object). 

         </p>
         <p>
          <b>Transfers of Personal Data:</b> PawPlate may transfer your personal data to domestic or foreign third parties in the following cases: 

         </p>
         <ul>
          <li>
            To our service providers (encargados) acting on our behalf and under our instructions, for purposes consistent with those described (e.g., cloud hosting, payment processing, email delivery, etc.). These are not considered “transfers” under Mexican law but rather usage by us through others (remisión). They do not require your consent, but we ensure such providers safeguard your data and use it only for what we dictate. 

          </li>
          <li>
             If we were to transfer data to an independent third party (another controller) – e.g., in a corporate transaction or with a strategic partner – we would do so only in compliance with LFPDPPP. Many types of transfers require either that the receiving party assumes the same obligations of data protection (which we would contractually ensure) or that we obtain your consent unless an exception applies (like transfer
within a corporate group under same processes, or needed for contract with you, or for law enforcement, etc.). 

          </li>
         </ul>
         <p>
          Currently, we do not make transfers of your personal data to third parties for their own use, except as might be required by authorities or in a merger/acquisition scenario as described. We do not sell your data. In the event we ever need to share data with a third party outside of the exceptions, we will request your consent. 

         </p>
         <p>
          <b>ARCO Rights:</b> Under Mexican law, you have the following rights with respect to your personal data: 

         </p>
         <ul>
          <li>
            <b>
Access:
            </b> To know what personal data we have about you, what we use it for, and the conditions of use (this privacy notice provides much of that, and you can also request the specific data we have stored). 

          </li>
           <li>
            <b>
              Rectification:
            </b> To request correction of your personal data if it is outdated, inaccurate, or incomplete. 

          </li>
           <li>
            <b>
              Cancellation:
            </b> To request we delete your personal data when you believe it is not being used according to the law or for the purposes consented, or when you simply want it removed (assuming it’s not in a situation where we must keep it). 

          </li>
           <li>
            <b>
              Opposition:
            </b>  To oppose the use of your personal data for specific purposes, provided you have a legitimate reason. For instance, you might oppose certain processing if you believe it causes you harm or wasn’t consented. 

          </li>
         </ul>
         <p>
          Collectively, these are known as ARCO rights. To exercise any of these, please submit a request to us (see Contact Us) with the following: 

         </p>
         <ul>
          <li>
Your full name and contact information (address or email) so we can respond.

          </li>
          <li>
             A document or information that proves your identity (e.g., copy of your ID) or, if an agent is acting for you, proof of their authority via power of attorney or equivalent.

          </li>
          <li>
             A clear and precise description of the personal data involved and the right you wish to exercise (access, rectification specifying what needs correction, cancellation with perhaps reason, or opposition explaining to which processing and why). 

          </li>
          <li>
            For rectification, any supporting documentation that validates the correct information (e.g., if you want to change your name, provide the correct spelling via official ID). 

          </li>
         </ul>
         <p>
          If we need more information to process your request, we will contact you within 5 business days of receiving your request to clarify. You’ll then have 10 business days to provide the missing info; otherwise, we may have to deny the request as insufficient, but you can submit again later. 

         </p>
         <p>
          We will respond to your request within 20 business days from when it was fully received. If your request is granted, we will make the changes or provide access within 15 business days after our response, per Mexican law timelines. So overall, the process could take up to 35 business days (roughly 7 weeks) from your initial request to completion, although we strive to be faster. 

         </p>
         <p>
          Our response will be sent via the means you prefer (email or postal mail) or the same means by which you made the request if not specified. If we deny your request (fully or partially), we will explain why. Common reasons for denial might include: we cannot grant cancellation because a law requires we keep the data, or opposition is not applicable because we demonstrated legitimate reasons to continue processing, etc. If opposing, note that some processing might be required to provide service, so if you oppose those, we might have to terminate service.
If you disagree with our response, you have the right to initiate a “Revision Procedure” (recurso de revisión) before Mexico’s data protection authority, the INAI (Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales). You must do this within 15 business days from the date we respond (or should have responded). The INAI has procedures to guide you, and we can provide info on that if needed. 

         </p>
         <p>
          <b>Revoking Consent:</b> In cases where you gave consent for processing, you can also revoke your consent if you wish to stop us from further processing your data. (For example, you consented to secondary uses like marketing, or if any data required express consent). You can make a consent revocation request through the same channel as ARCO. We will evaluate your request and inform you of consequences – sometimes, revoking consent for certain uses (like core service data) might mean we cannot continue providing you service. Where that’s the case, we’ll let you decide if you indeed want to revoke. If you confirm, we will cease processing that data and, if it means closing your account, we’ll do so. 
         </p>
         <p>
          <b>
            Limiting Use or Disclosure: 
          </b> Apart from ARCO, the law encourages giving options to limit use/disclosure of data. You can: 

         </p>
         <ul>
          <li>
Register in our opt-out list (Lista de Exclusión) if you don’t want to receive marketing communications. We maintain an internal list of individuals who have opted out of promotions, and we honor that. 

          </li>
          <li>
            Use available settings (like turning off certain features or communications in-app) to limit how your info is shared or visible. 

          </li>
         </ul>
         <p> 
          <b>
            Cookies/Web Beacons:
          </b> Mexican guidelines say we should inform you if we use cookies, web beacons, or similar, and how you can disable them. We have described cookie use in detail in Cookies and Tracking Technologies. To reiterate: we use cookies to improve your experience. Data collected may include your browser type, OS, pages visited, etc. You can disable cookies via your browser settings (each browser has its method). Disabling cookies might affect functionality (like staying logged in). By using our site without disabling cookies, we presume you consent to their use. 

         </p>
         <p> 
          <b>
            Changes to Privacy Notice (Aviso de Privacidad): 
          </b> If we change this privacy notice (for Mexico, a material change could be e.g. new purposes, new data being collected, changes in identity of the business, or changes in how to exercise rights), we will communicate those changes. We will always indicate the date of the last update (see top of Policy). We keep older versions available if needed for reference. 

         </p>
         <p>
          <b>
            By having read this far, we want to thank you for caring about how your data is handled! 

          </b>
         </p>
         <p>
          PawPlate is committed to privacy compliance across all these jurisdictions and more. If you have any question about any section of this Policy or your rights under any specific law, do not hesitate to reach out to us at support@pawplate.io. Your trust and confidence in PawPlate is of utmost importance to us, and we will do everything we can to maintain it through transparent and responsible data practices.

         </p>

      </div>
    </section>
  );
};

export default PrivacyPolicy;
